import * as THREE from "three";
import {Camera, Object3D, Scene} from "three";
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
// import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import { Unsubscribe } from "@reduxjs/toolkit";
import MaterialAsset from './MaterialAsset';
import AudioSource from "./AudioSource";
import {checkShortcutInput} from './ShortcutHandler';
import ScopeCamera from "./Camera";
import Text3D from "./Text3D";
import ARTracker from "./ARTracker";
import DefaultSceneManager from './DefaultSceneManager';
import {GroupsManager} from "./GroupsManager";
import Utils from './Utilities';
import Scenario from "./scenarioElements/Scenario";
import ToolsManager from "./tools/ToolsManager";
import Callout from "./Callout";
import SceneRecorder from './SceneRecorder';
import InteractiveModeManager from "./InteractiveModeManager";
import WorkspaceManager from "./WorkspaceManager";
import {assert, ErrorCategory, getErrorMessage} from "./utilities/ErrorHandling";
import AssetsManager from "./AssetsManager";
import StepConditionsManager from "./StepConditionsManager";
import QuizManager from "./QuizManager";
import PostProcessManager from "./PostProcessManager";
import SceneObjectUtils from "./utilities/SceneObjectUtils";
import TransformUtils from "./utilities/TransformUtils";
import MathUtils from "./utilities/MathUtils";
import SceneCameraController from "./SceneCameraController";
import ScenarioHealthUtils from "./utilities/ScenarioHealthUtils";
import {MetadataManager} from "./MetadataManager";
import PartsCatalogUtils from "./utilities/PartsCatalogUtils";
import {Timeline} from "./timeline/Timeline";
import MediaPanel from "./MediaPanel";
import ProjectSaver from './ProjectSaver';
import Input from './Input';
import ScopeLight from './Light';
import Time from './Time';
import VisibilityManager from './VisibilityManager';
import { CustomMaterials } from "./CustomMaterials";
import BillboardManager from "./BillboardManager";
import GizmoManager from "./GizmoManager";
import TriggerManager from "./TriggerManager";
import PickingManager from "./PickingManager";
import {GLTFLoader} from './GLTFLoader';
import ActionManager from './ActionManager';
import Step from "./scenarioElements/Step";
import CubemapUtils from "./utilities/CubemapUtils";
import { getUuidv4 } from "./utilities/Misc";
import {APIMethod } from "./api/Decorators";
import AddObjectAction from '../actions/AddObjectAction';
import AddARTrackerAction from '../actions/AddARTrackerAction';
import {
	ApplicationModuleTypes,
	CameraProjectionModes,
	CustomEventTypes,
	defaultBillboardObject,
	defaultPivot,
	LightTypes,
	MaterialTypes,
	PrimitiveTypes,
	RenderLayers,
	SimulationStates,
	WorldUnitScales
} from '../globals';
import TimelineEditorUtilities from "../components/timeline/TimelineEditorUtilities";
import {VisibilityModes} from "../interfaces/enums";
import store, {StoreActions} from '../state/store';
import {ISceneParams} from '../interfaces/ScenarioElements';
import {BuiltInCubeMaps, ISceneViewData} from "../interfaces/Misc";
import {viewports} from "../components/viewport/Viewport";
import StandbyOverlay from "../components/layout/StandbyOverlay";

type IModelTrackerPreviewRenderData = {
	tracker: ARTracker,
	camera: THREE.PerspectiveCamera,
	context: CanvasRenderingContext2D
}

type renderStats = {
	triangles: number,
	calls: number
}

export default class Engine
{
	private static instance: Engine;

	private renderer: THREE.WebGLRenderer;
	private renderStats: renderStats;
	private _backgroundScene: THREE.Scene;
	private currentScene? : THREE.Scene;
	private objectPreviewScene: THREE.Scene;
	private skyboxCamera: THREE.PerspectiveCamera;
	private backgroundColor: THREE.Color;
	private displayUnitScale: WorldUnitScales;
	private sceneList: THREE.Scene[] = [];
	private objectMixerMap: Map<string, THREE.AnimationMixer> = new Map();
	private animatedObjects: Set<THREE.Object3D> = new Set();
	private modelBank: Map<string, THREE.Object3D> = new Map();
	private defaultInteractiveCamera: ScopeCamera;
	private defaultStateLights: ScopeLight[];
	private audioListener: THREE.AudioListener;
	private grid: THREE.Mesh;

	private activeSceneViewId?: string;
	private renderCamera?: Camera;
	private previewCamera?: ScopeCamera;
	private unsubscribe?: Unsubscribe;	
	private environmentMap: THREE.CubeTexture | null = null;
	private legacyLightingMode: boolean;
	private renderBackgroundMap: boolean;
	get envMapAssetId() { return this.environmentMap ? this.environmentMap["assetId"] : ""; }
	private _backgroundMap: THREE.CubeTexture | null = null;
	private currentStep?: Step;
	private pendingStepChange? : Step;

	private sceneRecorder: SceneRecorder;
	private pickingManager: PickingManager;
	private triggerManager: TriggerManager;
	private gizmoManager: GizmoManager;
	private toolsManager: ToolsManager;
	private billboardManager: BillboardManager;
	private interactiveModeManager: InteractiveModeManager;
	private postProcessManager: PostProcessManager;

	private viewportCameraPerspective: THREE.PerspectiveCamera;
	private viewportCameraOrthographic: THREE.OrthographicCamera;
	private mainSceneCameraSettings?: ISceneViewData;

	focusObject: THREE.Object3D | null = null;

	static getInstance() : Engine
	{
		if (!Engine.instance) Engine.instance = new Engine();
		return Engine.instance;
	}

	constructor()
	{
		this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance", stencil: false, alpha: true });
		//this.renderer.setPixelRatio(window.devicePixelRatio); <--- bad line of code
		this.renderer.autoClear = false;
		this.renderStats = { triangles: 0, calls: 0 };

		this._backgroundScene = new THREE.Scene();
		this.renderBackgroundMap = false;
		this.skyboxCamera = new THREE.PerspectiveCamera(90, 2.0, 0.1, 1);
		this.objectPreviewScene = new THREE.Scene();
		this.objectPreviewScene.overrideMaterial = CustomMaterials.getGhostMaterial();
		this.backgroundColor = new THREE.Color(0xbbbbbb);
		this.displayUnitScale = WorldUnitScales.Meters;
		this.viewportCameraPerspective = new THREE.PerspectiveCamera(60, 1, 0.01, 10000);
		this.viewportCameraPerspective.layers.enable(RenderLayers.GridLayer);
		this.viewportCameraOrthographic = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.001, 10000);
		this.viewportCameraOrthographic.layers.enable(RenderLayers.GridLayer);
		this.audioListener = new THREE.AudioListener();
		this.displayUnitScale = WorldUnitScales.Meters;
		this.legacyLightingMode = true;

		this.grid = new THREE.Mesh(new THREE.PlaneGeometry(1000, 1000), CustomMaterials.getGridMaterial());
		this.grid.rotateX(THREE.MathUtils.degToRad(-90));
		this.grid.layers.enable(RenderLayers.MainCameraOnly);
		this.grid.layers.enable(RenderLayers.GridLayer);
		this.grid.renderOrder = -3;

		Time.init();
		Input.init();
		ARTracker.init();
		QuizManager.init();
		StepConditionsManager.init();
		ScenarioHealthUtils.init();

		this.sceneRecorder = new SceneRecorder();
		this.pickingManager = new PickingManager();
		this.triggerManager = new TriggerManager();
		this.gizmoManager = new GizmoManager();
		this.toolsManager = new ToolsManager();
		this.billboardManager = new BillboardManager();
		this.interactiveModeManager = new InteractiveModeManager();
		this.postProcessManager = new PostProcessManager();

		this.defaultInteractiveCamera = new ScopeCamera();
		this.defaultInteractiveCamera.name = "Interactive Mode Camera";
		this.defaultInteractiveCamera.position.copy(SceneCameraController.homePosition);
		this.defaultInteractiveCamera.lookAt(0, 0, 0);
		this.defaultInteractiveCamera.quaternion.multiply(new THREE.Quaternion(0, 1, 0, 0));
		this.defaultInteractiveCamera.setCameraIsInteractiveMode(true);
		SceneObjectUtils.initialiseObjectProperties(this.defaultInteractiveCamera);

		this.defaultStateLights = [];
		this.defaultStateLights.push(new ScopeLight(LightTypes.Directional));
		this.defaultStateLights.push(new ScopeLight(LightTypes.Directional));
		this.defaultStateLights[0].setLightIntensity(3);
		this.defaultStateLights[0].position.set(2, 5, 0);
		this.defaultStateLights[0].rotation.set(THREE.MathUtils.degToRad(-85.0), THREE.MathUtils.degToRad(30.0), THREE.MathUtils.degToRad(10.0));
		this.defaultStateLights[1].setLightIntensity(0.5);
		this.defaultStateLights[1].setLightColor('#b0b0b0');
		this.defaultStateLights[1].position.set(-2, 5, 0);
		this.defaultStateLights[1].rotation.set(THREE.MathUtils.degToRad(-55.0), THREE.MathUtils.degToRad(-35.0), THREE.MathUtils.degToRad(165.0));
		SceneObjectUtils.initialiseObjectProperties(this.defaultStateLights[0]);
		SceneObjectUtils.initialiseObjectProperties(this.defaultStateLights[1]);
	}

	get backgroundMap() { return this._backgroundMap; }
	set backgroundMap(map: THREE.CubeTexture | null)
	{
		this._backgroundMap = map;

		// Keep backgroundScene in sync with the newly assigned map so tests and runtime stay consistent.
		if (map)
		{
			const renderBackgroundMap = store.getState().preferencesModule.editorPreferences.renderDefaultBackground;
			this._backgroundScene.background = renderBackgroundMap ? map : new THREE.Color(this.backgroundColor);
		}
		else
		{
			// Reset to plain color when no cubemap is provided
			this._backgroundScene.background = new THREE.Color(this.backgroundColor);
		}
	}
	get backgroundScene() { return this._backgroundScene; }

	updateFocusObject = (scene?: Scene) =>
	{
		if (store.getState().applicationModule.simulationState !== SimulationStates.Playing || !store.getState().applicationModule.simulationAsViewer)
		{
			this.focusObject = null;
			return;
		}
		const currentScene = scene ?? this.getCurrentScene();
		if (currentScene)
		{
			const interactiveCamera = ScopeCamera.getInteractiveModeCamera(currentScene);
			const focusPointGUID = interactiveCamera?.userData.interactiveCameraFocusPoint;
			// Check if focusPointGUID is a non-empty string before attempting to find the object
			if (focusPointGUID && typeof focusPointGUID === 'string' && focusPointGUID.length > 0) 
			{
				this.focusObject = SceneObjectUtils.findObjectByGuid(currentScene, focusPointGUID);
			}
			else 
			{
				this.focusObject = null;
			}
		}
	}

	init = async () =>
	{
		document.addEventListener(CustomEventTypes.simulationStarted, (e) =>
		{
			const mainSceneView = this.getMainSceneView();
			if(mainSceneView)
			{
				const sceneViewData = WorkspaceManager.getSceneViewData(mainSceneView.windowId);
				if (sceneViewData?.activeCamera instanceof THREE.OrthographicCamera)
				{
					mainSceneView.setCameraProjectionMode(CameraProjectionModes.Perspective, true);
				}
				
				const audioListener = this.getAudioListener();
				mainSceneView.activeCamera.add(audioListener);
			}
			this.updateFocusObject();
		});

		document.addEventListener(CustomEventTypes.simulationStopped, (e) =>
		{
			const audioListener = this.getAudioListener();
			if(audioListener?.removeFromParent)
			{
				audioListener.removeFromParent();
			}
			this.focusObject = null;
		});

		//the original implementation of these world-getters extracts rotation/scale by using matrix.decompose, which fails when the matrix contains non-uniform scale.
		//these overrides are a more expensive alternative, but will work under any circumstances.
		THREE.Object3D.prototype.getWorldQuaternion = function (target)
		{
			target = target || new THREE.Quaternion();
			target.copy(this.quaternion);
			let parent = this.parent;
			while(parent)
			{
				target.premultiply(parent.quaternion);
				parent = parent.parent;
			}
			return target;
		}

		THREE.Object3D.prototype.getWorldScale = function (target)
		{
			target = target || new THREE.Vector3();
			target.copy(this.scale);
			let parent = this.parent;
			while(parent)
			{
				target.multiply(parent.scale);
				parent = parent.parent;
			}
			return target;
		}

		await this.preloadAssets();
		Engine.getInstance().setEnvironmentMap(CubemapUtils.getCubemap());
		Engine.getInstance().setBackgroundMap(CubemapUtils.getCubemap());

		// respond to redux changes
		this.unsubscribe = store.subscribe(() =>
		{
			// viewport background map
			const renderDefaultBackground = store.getState().preferencesModule.editorPreferences.renderDefaultBackground;
			if(renderDefaultBackground !== this.renderBackgroundMap)
			{
				this.renderBackgroundMap = renderDefaultBackground;
				if(this.renderBackgroundMap && this._backgroundMap)
				{
					this._backgroundScene.background = this._backgroundMap;
				}
			}

			// viewport background color
			const customBackgroundColor = store.getState().preferencesModule.editorPreferences.viewportBackgroundColor;
			const hexColor = parseInt("0x" + customBackgroundColor.substring(1));
			if(this.backgroundColor.getHex() !== hexColor)
			{
				this.backgroundColor.setHex(hexColor);
				if(!this.renderBackgroundMap) 
				{
					this._backgroundScene.background = this.backgroundColor;
				}
			}

			// project settings - environment
			const envMapId = store.getState().preferencesModule.projectSettings.environmentMapId;
			const environmentMap = CubemapUtils.getCubemap(envMapId);
			if(environmentMap && this.environmentMap !== environmentMap)
			{
				this.setEnvironmentMap(environmentMap);
			}
		});
	}

	mainLoop = () => 
	{
		try 
		{
			this.update();
			this.render();
			requestAnimationFrame(this.mainLoop);
		}
		catch (error) 
		{
			assert(false, `Engine main loop error: ${getErrorMessage(error)}`, ErrorCategory.General);
		}
	}

	update = () =>
	{
		Time.update();

		if (this.pendingStepChange) this.loadPendingStepScene();

		if (this.getCurrentScene())
		{
			const poseEditorVisible = WorkspaceManager.getModelTrackerPoseEditorData().context;
			const activeSceneView = this.getActiveSceneView();
			const activeSceneViewVisible = activeSceneView && activeSceneView.context?.canvas?.clientHeight > 0 && activeSceneView.context?.canvas?.clientWidth > 0;
			if (Utils.IsSimulationRunning() || store.getState().timelineModule.editorData.isPlaying)
			{
				const partsCatalogOpen = PartsCatalogUtils.isPartsCatalogOpen();
				const timeline = this.currentStep!.timeline;
				if (timeline && !partsCatalogOpen)
				{
					const targetTime = MathUtils.round((timeline.currentTime + Time.getDeltaTime()) % timeline.loopTime, 3);
					timeline.update(targetTime);
					store.dispatch(StoreActions.timeline.setCurrentFrame(TimelineEditorUtilities.getFrameFromTime(targetTime)));
				}

				if (activeSceneViewVisible)
				{
					activeSceneView.cameraController.update();
					if (Utils.IsSimulationRunning())
					{
						this.getTriggerManager().update();
						this.getBillboardManager().update();
						QuizManager.update();
						StepConditionsManager.update();
					}
				}
			}
			else if (poseEditorVisible)
			{
				this.getGizmoManager().update();
				WorkspaceManager.getModelTrackerPoseEditorData().updateFunction();

			}
			else if (activeSceneViewVisible)
			{
				this.sceneRecorder.update();
				this.getGizmoManager().update();
				if (!this.getGizmoManager().isBusy())
				{
					activeSceneView.cameraController.update();
					if (!activeSceneView.cameraController.busy)
					{
						this.getToolsManager().update();
						if (!this.getToolsManager().isBusy())
						{
							this.getPickingManager().update();
						}
					}
				}
				this.getInteractiveModeManager().update();
			}

			for (const sceneViewData of WorkspaceManager.getAllSceneViewsData())
			{
				if (!sceneViewData.context) continue;
				if(sceneViewData.renderViewCube) sceneViewData.viewCube?.update();
			}

			checkShortcutInput();
		}

		Input.update();
	}

	render = () =>
	{
		if (!this.currentScene) return;

		// update render stats
		if (this.getRenderer().info.render.triangles !== this.renderStats.triangles)
		{
			this.renderStats.triangles = this.getRenderer().info.render.triangles;
			this.renderStats.calls = this.getRenderer().info.render.calls;
			document.dispatchEvent(new CustomEvent(CustomEventTypes.viewTriCountChanged));
		}

		const initialGizmoCamera = this.getGizmoManager().getCamera();

		this.renderModelTrackerPreviews();

		this.renderObjectPreviews();

		// render preview or cameras that are explicitly marked for rendering
		const sceneCameras = ScopeCamera.getSceneCameras(this.getCurrentScene());
		for (const camera of sceneCameras)
		{
			const shouldRenderCamera = camera.updateCanvas || camera === this.previewCamera;
			if (shouldRenderCamera)
			{
				this.getRenderer().setSize(camera.canvas.width, camera.canvas.height);
				this.getRenderer().clear();
				if (camera.getCameraProjectionMode() === CameraProjectionModes.Perspective)
				{
					camera.updateMatrixWorld();
					this.getRenderer().render(this._backgroundScene, camera.perspectiveCamera);
				}
				else
				{
					this.skyboxCamera.rotation.copy(camera.rotation);
					this.getRenderer().render(this._backgroundScene, this.skyboxCamera);
				}

				this.renderCamera = camera.currentCamera;
				this.getRenderer().render(this.currentScene, camera.currentCamera);
				const context = camera.canvas.getContext('2d');
				context?.drawImage(this.getRenderer().domElement, 0, 0);
				document.dispatchEvent(new CustomEvent(CustomEventTypes.cameraRendered, { detail: { cameraId: camera.userData.guid } }));

				if (camera === this.previewCamera)
				{
					const cameraPreviewContext = WorkspaceManager.getCameraPreviewData()?.context;
					if (cameraPreviewContext)
					{
						cameraPreviewContext.drawImage(camera.canvas, 0, 0, camera.canvas.width, camera.canvas.height,
							0, 0, cameraPreviewContext.canvas.width, cameraPreviewContext.canvas.height);
					}
				}
			}
		}

		// render the scene view cameras
		const sceneViewDatas = WorkspaceManager.getAllSceneViewsData();
		for (const sceneViewData of sceneViewDatas)
		{
			if (!sceneViewData.context || !sceneViewData.context.canvas.clientHeight || !sceneViewData.context.canvas.clientWidth) continue;

			this.getRenderer().setSize(sceneViewData.context.canvas.clientWidth, sceneViewData.context.canvas.clientHeight);
			this.getRenderer().clear();
			
			// render the scene background
			if(sceneViewData.activeCamera.type ==='PerspectiveCamera' && !sceneViewData.cameraController.animateProjection)
			{
				this.getRenderer().render(this._backgroundScene, sceneViewData.activeCamera);
			}
			else
			{
				this.skyboxCamera.rotation.copy(sceneViewData.activeCamera.rotation);
				this.getRenderer().render(this._backgroundScene, this.skyboxCamera);
			}

			if(sceneViewData.isMainSceneView || sceneViewData === this.getMainSceneView())
			{
				this.mainSceneCameraSettings = { ...sceneViewData };
			}

			const sceneViewId = sceneViewData.windowId;
			this.getGizmoManager().setCamera(sceneViewData.activeCamera);
			this.getToolsManager().preRender(sceneViewId);
			this.renderCamera = sceneViewData.activeCamera;
			this.getRenderer().render(this.currentScene, sceneViewData.activeCamera);
			this.getPostProcessManager().render(sceneViewId);
			this.getRenderer().clear(false, true, false);
			if(sceneViewData.renderViewCube)
			{
				sceneViewData.viewCube.render();
			}
			sceneViewData.context.drawImage(this.getRenderer().domElement, 0, 0);
			this.getToolsManager().render(sceneViewId);
		}
		
		// render the main scene camera into any standalone viewport canvases
		for(const viewportCanvas of viewports)
		{
			const viewportContext = viewportCanvas?.getContext('2d');
			if(!viewportContext || !viewportCanvas.clientWidth || !viewportCanvas.clientHeight) continue;

			this.getRenderer().setSize(viewportContext.canvas.clientWidth, viewportContext.canvas.clientHeight);
			this.getRenderer().clear();

			let activeCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
			const aspectRatio = viewportCanvas.clientWidth / viewportCanvas.clientHeight;

			// in case the scene view was never rendered to begin with, use some default values
			if(!this.mainSceneCameraSettings)
			{
				activeCamera = this.viewportCameraPerspective;
				activeCamera.aspect = aspectRatio;
				activeCamera.updateProjectionMatrix();
				activeCamera.position.copy(SceneCameraController.homePosition);
				activeCamera.lookAt(0, 0, 0);

				this.getRenderer().render(this._backgroundScene, activeCamera);
			}
			else
			{
				if(this.mainSceneCameraSettings.activeCamera instanceof THREE.PerspectiveCamera && !this.mainSceneCameraSettings.cameraController.animateProjection)
				{
					activeCamera = this.viewportCameraPerspective;
					activeCamera.aspect = aspectRatio;
					activeCamera.fov = this.mainSceneCameraSettings.activeCamera.fov;
					activeCamera.updateProjectionMatrix();
					activeCamera.position.copy(this.mainSceneCameraSettings.activeCamera.position);
					activeCamera.quaternion.copy(this.mainSceneCameraSettings.activeCamera.quaternion);

					this.getRenderer().render(this._backgroundScene, activeCamera);
				}
				else
				{
					activeCamera = this.viewportCameraOrthographic;
					activeCamera.left = aspectRatio * -0.5;
					activeCamera.right = aspectRatio * 0.5;
					activeCamera.updateProjectionMatrix();
					activeCamera.position.copy(this.mainSceneCameraSettings.activeCamera.position);
					activeCamera.quaternion.copy(this.mainSceneCameraSettings.activeCamera.quaternion);

					this.skyboxCamera.rotation.copy(this.mainSceneCameraSettings.activeCamera.rotation);
					this.getRenderer().render(this._backgroundScene, this.skyboxCamera);
				}
			}

			this.getRenderer().render(this.currentScene, activeCamera);
			viewportContext.drawImage(this.getRenderer().domElement, 0, 0);
		}

		if (initialGizmoCamera)
		{
			this.getGizmoManager().setCamera(initialGizmoCamera);
		}
	}

	renderModelTrackerPreviews = () =>
	{
		const modelTrackersPreviewRenderData = WorkspaceManager.getModelTrackerPreviewRenderData() as IModelTrackerPreviewRenderData[];
		if (modelTrackersPreviewRenderData)
		{
			for (const renderData of Object.values(modelTrackersPreviewRenderData))
			{
				if (renderData.context)
				{
					const poseEditorData = WorkspaceManager.getModelTrackerPoseEditorData();
					const poseEditorVisible = poseEditorData.context && poseEditorData.tracker.userData.guid === renderData.tracker.userData.guid;

					const trackersVisible = ARTracker.getTrackersVisibility();
					if (!trackersVisible) ARTracker.setTrackersVisibility(true);
					const previewHeight = 512;
					const previewWidth = previewHeight * renderData.camera.aspect;
					if (poseEditorVisible || renderData.camera.userData.renderIsDirty)
					{
						if (poseEditorVisible) this.getGizmoManager().setCamera(poseEditorData.tracker.modelTrackerCamera);
						this.getRenderer().setSize(previewWidth, previewHeight);
						this.getRenderer().clear();
						this.getRenderer().render(renderData.tracker.posePreviewScene, renderData.camera);
					}

					const previewCanvasWidth = renderData.context.canvas.width;
					const previewCanvasHeight = renderData.context.canvas.height;
					const previewAspect = previewCanvasWidth / previewCanvasHeight;
					if (renderData.camera.userData.renderIsDirty)
					{
						let sx: number, sy: number, sWidth: number, sHeight: number;

						if (previewAspect > renderData.camera.aspect)
						{
							sWidth = previewWidth;
							sHeight = sWidth / previewAspect;
							sx = 0;
							sy = (previewHeight - sHeight) / 2;
						}
						else
						{
							sHeight = previewHeight;
							sWidth = sHeight * previewAspect;
							sx = (previewWidth - sWidth) / 2;
							sy = 0;
						}
						renderData.context.clearRect(0, 0, previewCanvasWidth, previewCanvasHeight);
						renderData.context.drawImage(this.getRenderer().domElement, sx, sy, sWidth, sHeight, 0, 0, previewCanvasWidth, previewCanvasHeight);
					}

					if (poseEditorVisible)
					{
						const canvasWidth = poseEditorData.context.canvas.width;
						const canvasHeight = poseEditorData.context.canvas.height;
						poseEditorData.context.clearRect(0, 0, canvasWidth, canvasHeight);
						poseEditorData.context.drawImage(this.getRenderer().domElement, 0, 0, canvasWidth, canvasHeight);
						poseEditorData.context.strokeStyle = "#eea904";
						poseEditorData.context.beginPath();
						poseEditorData.context.setLineDash([4, 4]);

						let x: number, y: number, width: number, height: number;

						if (previewAspect > renderData.camera.aspect)
						{
							width = previewWidth;
							height = previewWidth / previewAspect;
							x = 0;
							y = (previewHeight - height) / 2;
						}
						else
						{
							height = previewHeight;
							width = previewHeight * previewAspect;
							x = (previewWidth - width) / 2;
							y = 0;
						}

						poseEditorData.context.strokeRect(x, y, width, height);

						if (!trackersVisible) ARTracker.setTrackersVisibility(false);
						return;
					}

					if (!trackersVisible) ARTracker.setTrackersVisibility(false);
				}
			}

			//multiple canvases might refer to the same camera, must set isDirty to false only after all canvases were rendered
			for (const renderData of Object.values(modelTrackersPreviewRenderData))
			{
				if (renderData.context && renderData.camera.userData.renderIsDirty)
				{
					renderData.camera.userData.renderIsDirty = false;
				}
			}
		}
	}

	renderObjectPreviews = () =>
	{
		const objectPreviewsData = WorkspaceManager.getObjectPreviewRenderData();
		for (const previewData of Object.values(objectPreviewsData))
		{
			if (previewData.context && previewData.camera.userData.renderIsDirty)
			{
				const originalParents = new Map();
				const parentsToChildren = new Map();
				for (const object of previewData.objects)
				{
					originalParents.set(object, object.parent);
					if (!parentsToChildren.has(object.parent))
					{
						parentsToChildren.set(object.parent, [...object.parent!.children]);
					}

					this.objectPreviewScene.attach(object);
				}

				this.getRenderer().setSize(previewData.context.canvas.width, previewData.context.canvas.height);
				this.getRenderer().clear();
				this.getRenderer().render(this.objectPreviewScene, previewData.camera);

				for (const [object, parent] of originalParents.entries())
				{
					parent.attach(object);
				}
				for (const [object, childrenArray] of parentsToChildren.entries())
				{
					object.children = childrenArray;
				}
				previewData.context.clearRect(0, 0, previewData.context.canvas.width, previewData.context.canvas.height);
				previewData.context.drawImage(this.getRenderer().domElement, 0, 0);
				previewData.camera.userData.renderIsDirty = false;
			}
		}
	}

	createScene = (params: ISceneParams) : string =>
	{
		const scene = new THREE.Scene();
		scene.userData.guid = params.existingGuid ? params.existingGuid : getUuidv4();
		this.sceneList.push(scene);

		const sourceScene = this.getScene(params.sourceSceneGuid);
		if (sourceScene)
		{
			const trackerRoot = ARTracker.getTrackerRoot();
			const trackerRootParent = trackerRoot.parent;
			if (trackerRootParent) trackerRootParent.children.splice(trackerRootParent.children.indexOf(trackerRoot), 1);
			const selectionRoot = SceneObjectUtils.cloneObject(Engine.getInstance().getSelectionRoot(sourceScene.userData.guid), true, params.isContinueScene, scene.userData.guid, params.sourceStepGuid);
			scene.add(selectionRoot);
			if (params.isDefaultScene)
			{
				DefaultSceneManager.getInstance().setDefaultScene(scene, sourceScene);
			}
			else if (params.defaultSceneId)
			{
				const defaultScene = this.getScene(params.defaultSceneId)!;
				DefaultSceneManager.getInstance().onSceneCopy(scene, sourceScene, defaultScene);
			}

			if (trackerRootParent) trackerRootParent.children.splice(0, 0, trackerRoot);
		}
		else
		{
			if(params.sourceSceneGuid)
			{
				console.error("Source scene is defined, but was not found: ", params.sourceSceneGuid);
			}

			const selectionRoot = new THREE.Object3D();
			selectionRoot.name = 'root';
			SceneObjectUtils.initialiseObjectProperties(selectionRoot);
			scene.add(selectionRoot);

			if (params.isDefaultScene)
			{
				DefaultSceneManager.getInstance().setDefaultScene(scene);

				if(this.legacyLightingMode)
				{
					for (const defaultLight of this.defaultStateLights)
					{
						const light = defaultLight.clone();
						this.addObject(light, selectionRoot, scene.userData.guid);
						ProjectSaver.getInstance().markAsSaveRoot(light);
					}
				}
			
				const interactiveCamera = this.defaultInteractiveCamera.clone();
				this.addObject(interactiveCamera, selectionRoot, scene.userData.guid);

				ProjectSaver.getInstance().markAsSaveRoot(interactiveCamera);
			}
		}

		if(params.isTrackersStep)
		{
			const interactiveCamera = ScopeCamera.getInteractiveModeCamera(scene);
			if(interactiveCamera)
			{
				this.removeObject(interactiveCamera, false, scene.userData.guid);
			}
		}

		return scene.userData.guid;
	}

	loadPendingStepScene = () : void =>
	{
		if (!this.pendingStepChange) return;
		if (this.currentStep?.timeline) this.currentStep.timeline.resetAnimations();

		for (const scene of this.sceneList)
		{
			if (scene.userData.guid === this.pendingStepChange.sceneId)
			{
				document.dispatchEvent(new CustomEvent(CustomEventTypes.onUnloadScene));
				scene.add(this.grid);
				this.currentScene = scene;
				scene.environment = this.environmentMap;
				ARTracker.onSceneChange();
				this.getPickingManager().onSceneChange();
				this.getToolsManager().onSceneChange();
				this.getInteractiveModeManager().onSceneChange(scene);
				this.getGizmoManager().setScene(scene);
				QuizManager.onStepChange(this.pendingStepChange);
				document.dispatchEvent(new CustomEvent(CustomEventTypes.hierarchyChangedEvent));
				document.dispatchEvent(new CustomEvent(CustomEventTypes.objectSelectionChanged));
				VisibilityManager.setObjectsVisibilityMode([this.getSelectionRoot()], [this.getSelectionRoot()!.userData.visibility[VisibilityManager.getRenderMode()]]);
				break;
			}
		}

		this.currentStep = this.pendingStepChange;
		this.pendingStepChange = undefined;
		if (this.currentStep?.timeline)
		{
			this.currentStep.timeline.update(0);
			store.dispatch(StoreActions.timeline.updateEditorState({ timelineId: this.currentStep.timeline.id, isRecording: false, currentFrame: 0, visibleEndFrame: (this.currentStep.timeline.loopTime + 1) * this.currentStep.timeline.fps }));
		}
		else
		{
			store.dispatch(StoreActions.timeline.updateEditorState({ isRecording: false, isPlaying: false }));
		}
	}

	setCurrentStep = (step: Step) =>
	{
		if(this.pendingStepChange)
		{
			this.loadPendingStepScene();
		}

		this.pendingStepChange = step;
	}

	addObject = (object: THREE.Object3D, parent: THREE.Object3D, sceneGuid? : string, forceMaintainName = false) =>
	{
		if (!sceneGuid) sceneGuid = this.currentScene!.userData.guid;
		const scene = this.getScene(sceneGuid!)!;

		if (!forceMaintainName) object.name = Utils.getUniqueElementName(parent.children, object.name);

		if (object.userData.removedFromScene)
		{
			parent.add(object);
		}
		else
		{
			parent.attach(object);
		}

		let triangleCount = 0; let descendantCount = 0;
		object.traverse((child) =>
		{
			delete child.userData.removedFromScene;
			triangleCount += SceneObjectUtils.getObjectTriangleCount(child);
			if (child !== object)
			{
				if (!child.userData.hideInHierarchy)
				{
					ScenarioHealthUtils.ObjectAdded(scene, child);
					descendantCount++;
				}
			}

			if (child.userData.animations)
			{
				for (const anim of child.userData.animations)
				{
					anim.sceneGuid = sceneGuid;
				}
				this.registerObjectAnimationsForExport(child);
			}

			if (child.userData.camera)
			{
				(child as ScopeCamera).onCameraAddedToScene(sceneGuid);
			}

			if (child.userData.groups)
			{
				for (const groupGuid of child.userData.groups)
				{
					GroupsManager.addToGroup(groupGuid, [child], sceneGuid);
				}
			}

			if (child.userData.billboard && child.userData.billboard.enabled)
			{
				Engine.getInstance().getBillboardManager().enableBillboard(child, sceneGuid);
			}

			if (child.userData.text3d)
			{
				Text3D.addToText3dBank(child.userData.guid, child as Text3D);
			}

			MaterialAsset.registerCustomMaterialReference(child);
		});

		ScenarioHealthUtils.ObjectAdded(scene, object, descendantCount, triangleCount);

		if (scene.userData.isDefaultScene)
		{
			DefaultSceneManager.getInstance().onDefaultObjectAdded(object, sceneGuid!);
		}

		if (!object.userData.hideInHierarchy)
		{
			VisibilityManager.setObjectsVisibilityMode([object], [object.userData.visibility[VisibilityManager.getRenderMode()]]);
			document.dispatchEvent(new CustomEvent(CustomEventTypes.hierarchyChangedEvent));
		}

		if(!(object instanceof ARTracker)) AssetsManager.addAssetsUsedBySceneObject(object);
	}

	removeObject = (object: THREE.Object3D, notifyDeafultState = false, sceneGuid? : string) =>
	{
		assert(!object.userData.removedFromScene);
		if (!sceneGuid) sceneGuid = this.currentScene!.userData.guid;
		if (notifyDeafultState) DefaultSceneManager.getInstance().syncObjectRemoval(object);

		object.parent!.remove(object);
		let triangleCount = 0; let descendantCount = 0;
		object.traverse((child) =>
		{
			triangleCount += SceneObjectUtils.getObjectTriangleCount(child);
			if (child !== object)
			{
				if (!child.userData.hideInHierarchy)
				{
					ScenarioHealthUtils.ObjectRemoved(this.getScene(sceneGuid!), child);
					descendantCount++;
				}
			}

			child.userData.removedFromScene = true;

			if (child.userData.animations)
			{
				this.deregisterObjectAnimationsForExport(child);
			}

			if (child.userData.camera)
			{
				(child as ScopeCamera).onCameraRemovedFromScene();
			}

			if (child.userData.groups)
			{
				for (const groupGuid of child.userData.groups)
				{
					GroupsManager.removeFromGroup(groupGuid, [child], sceneGuid, true);
				}
			}
		});

		ScenarioHealthUtils.ObjectRemoved(this.getScene(sceneGuid!), object, descendantCount * -1, triangleCount * -1);

		if(!(object instanceof ARTracker)) AssetsManager.removeAssetsUsedBySceneObject(object);

		document.dispatchEvent(new CustomEvent(CustomEventTypes.hierarchyChangedEvent));
	}

	@APIMethod({description: "Creates an empty object in the scene", parameters: {}, returnType: "Object3D"})
	createEmptyObject(parent?: Object3D, raiseAction = true) : THREE.Object3D
	{
		const object = new THREE.Object3D();
		object.name = 'Empty Object';
		SceneObjectUtils.initialiseObjectProperties(object);

		if(raiseAction)
		{
			new AddObjectAction(object);
		}
		else
		{
			ProjectSaver.getInstance().markAsSaveRoot(object);
			this.addObject(object, parent ?? this.getSelectionRoot()!);
		}

		return object;
	}

	@APIMethod({description: "Creates a light object in the scene.", parameters: {}, returnType: "Object3D"})
	createLight(type: LightTypes, parent?: Object3D, raiseAction = true): ScopeLight
	{
		const light = new ScopeLight(type);
		SceneObjectUtils.initialiseObjectProperties(light);

		if(raiseAction)
		{
			new AddObjectAction(light);
		}
		else
		{
			ProjectSaver.getInstance().markAsSaveRoot(light);
			this.addObject(light, parent ?? this.getSelectionRoot()!);
		}

		return light;
	}

	@APIMethod({
		description: "Creates a primitive object in the scene. Type can be \"Cube\", \"Sphere\", \"Plane\" or \"Cylinder\"",
		parameters: {type: "PrimitiveTypes"},
		returnType: "Object3D"
	})
	createPrimitive(type: PrimitiveTypes, parent?: Object3D, raiseAction = true): THREE.Mesh
	{
		const geometry = Utils.getPrimitiveGeometry(type, 0.1);
		const primitive = new THREE.Mesh(geometry);
		primitive.name = type;
		primitive.userData.guid = getUuidv4();
		primitive.userData.primitiveType = type;
		primitive.userData.primitiveScale = 0.1;
		primitive.layers.enable(RenderLayers.ObjectPreview);
		const defaultMaterialAsset = MaterialAsset.defaultMaterial;
		MaterialAsset.assignMaterialAssetToObject(defaultMaterialAsset, primitive);
		SceneObjectUtils.initialiseObjectProperties(primitive);

		switch (type)
		{
			case PrimitiveTypes.Cube:
				primitive.userData.pivot = JSON.parse(JSON.stringify(defaultPivot));
				TransformUtils.applyPivot(primitive, { x: 0, y: -0.05, z: 0 });
				primitive.position.set(0, 0, 0);
				break;
			case PrimitiveTypes.Cylinder:
				primitive.userData.pivot = JSON.parse(JSON.stringify(defaultPivot));
				TransformUtils.applyPivot(primitive, { x: 0, y: -0.1, z: 0 });
				primitive.position.set(0, 0, 0);
				break;
			case PrimitiveTypes.Plane:
				primitive.userData.billboard = JSON.parse(JSON.stringify(defaultBillboardObject));
				primitive.userData.billboard["offsetRotation"] = { x: 90, y: 0, z: 0 };
				break;
		}

		if(raiseAction)
		{
			new AddObjectAction(primitive);
		}
		else
		{
			ProjectSaver.getInstance().markAsSaveRoot(primitive);
			this.addObject(primitive, parent ?? this.getSelectionRoot()!);
		}

		return primitive;
	}

	createARTracker = (step?: Step) =>
	{
		const targetStep = step ?? Scenario.currentStep();
		targetStep && new AddARTrackerAction(targetStep);
	}

	@APIMethod({description: "Creates an audio source", parameters: {}, returnType: "AudioSource"})	
	createAudioSource (): AudioSource
	{
		const audioSource = new AudioSource(this.getAudioListener());
		SceneObjectUtils.initialiseObjectProperties(audioSource);
		new AddObjectAction(audioSource);
		return audioSource;
	}

	@APIMethod({description: "Creates a camera in the scene. The camera can be either interactive (user-controlled) or regular (programmatic).", parameters: {isInteractive: "boolean"}, returnType: "ScopeCamera"})
	createCamera(isInteractive: boolean = false): ScopeCamera
	{
		const camera = new ScopeCamera();
		SceneObjectUtils.initialiseObjectProperties(camera);

		// Create the AddObjectAction first to ensure the camera is properly added to the scene
		const addObjectAction = new AddObjectAction(camera);

		// Set interactive mode after the camera has been added to the scene
		if (isInteractive)
		{
			// Set interactive mode directly since the AddObjectAction constructor is already called
			camera.setCameraIsInteractiveMode(true);
		}

		return camera;
	}

	@APIMethod({description: "create3DText", parameters: {}, returnType: "Text3D"})
	create3DText(): Text3D
	{
		const textObject = new Text3D();
		SceneObjectUtils.initialiseObjectProperties(textObject);
		new AddObjectAction(textObject);
		return textObject;
	}

	createImagePanel = (assetId : string) =>
	{
		const texture = MaterialAsset.getTexture(assetId);
		if (texture)
		{
			const imagePanel = new MediaPanel({ assetId: assetId, createMaterial: true });
			SceneObjectUtils.initialiseObjectProperties(imagePanel);
			new AddObjectAction(imagePanel);
		}
		else
		{
			MaterialAsset.loadTexture(assetId).then((texture) =>
			{
				Engine.getInstance().createImagePanel(assetId);
				AssetsManager.addUsedAsset(assetId);
			});
		}
	}
	createVideoPanel = (assetId: string) =>
	{
		const texture = MaterialAsset.getVideoTexture(assetId);
		if (texture)
		{
			const videoPanel = new MediaPanel({ assetId: assetId, createMaterial: true});
			SceneObjectUtils.initialiseObjectProperties(videoPanel);
			new AddObjectAction(videoPanel);
		}
		else
		{
			MaterialAsset.loadVideoTexture(assetId).then((texture) =>
			{
				Engine.getInstance().createVideoPanel(assetId);
				AssetsManager.addUsedAsset(assetId);
			});
		}
	}

	/**
	 *
	 * @param id
	 * @param url
	 * @param name
	 * @param startingPosition
	 * @param addToScene
	 * @param generateMaterialAssets
	 * @param onDownloadProgress Reports on progress. Progress is between 0-1
	 * @param onComplete
	 */
	prepareModel(id: string, url: string, name: string,
		startingPosition? : THREE.Vector3,
		addToScene = true, generateMaterialAssets = true,
		onDownloadProgress? : (id: string, url: string, name: string, progress: number) => void,
		onComplete? : (id: string, url: string, name: string, model?: Object3D) => void) : Promise<void>
	{
		assert(!this.modelBank.has(id));
		if (name)
		{
			const idx = name.lastIndexOf('.');
			if (idx > 0)
			{
				name = name.substring(0, idx);
			}
		}

		return new Promise((resolve, reject) =>
		{
			// const ktx2Loader = new KTX2Loader()
			// 	.setTranscoderPath( '/gltf/draco/' )
			// 	.detectSupport( this.getRenderer() );
			const dracoLoader = new DRACOLoader()
				.setDecoderConfig({type: 'js'})
				.setDecoderPath( '/gltf/draco/' );
			new GLTFLoader()
				// .setKTX2Loader(ktx2Loader)
				.setDRACOLoader( dracoLoader )
				.load(url, (gltf) =>
				{
					StandbyOverlay.startStandbyMode("Loading Content");
					setTimeout(() =>
					{
						const bounds = new THREE.Box3();
						const boundSize = new THREE.Vector3();
						const boundsCenter = new THREE.Vector3();
						gltf.scene.updateMatrixWorld(true);
						let assetRoot: THREE.Object3D;
						assert(gltf.parser.json.scenes[0].nodes.length === gltf.scene.children.length);

						const convertOrGenerateModelRoot = (object: THREE.Object3D) : THREE.Object3D =>
						{
							object.userData.isModelRoot = true;
							const isCurrentlySubMesh = object.userData.isSubMesh;
							delete object.userData.isSubMesh;
							if (!object.userData.modelData) object.userData.modelData = {};

							if (object instanceof THREE.Mesh && object.geometry)
							{
								return object;
							}
							else
							{
								bounds.makeEmpty();
								bounds.expandByObject(object);
								bounds.getSize(boundSize);
								bounds.getCenter(boundsCenter);

								const geometry = new THREE.BoxGeometry(boundSize.x, boundSize.y, boundSize.z);
								geometry.translate(boundsCenter.x - object.position.x, boundsCenter.y - object.position.y, boundsCenter.z - object.position.z);
								geometry.applyQuaternion(new THREE.Quaternion().copy(object.quaternion).invert());
								geometry.scale(1 / object.scale.x, 1 / object.scale.y, 1 / object.scale.z);

								const newRoot = new THREE.Mesh(geometry, CustomMaterials.getModelRootMaterial());
								newRoot.userData = JSON.parse( JSON.stringify( object.userData ) );
								if(!isCurrentlySubMesh) newRoot.userData.modelData = {};
								newRoot.name = object.name;

								newRoot.position.copy(object.position);
								newRoot.rotation.copy(object.rotation);
								newRoot.scale.copy(object.scale);

								if(object.onAfterRender) newRoot.onAfterRender = object.onAfterRender;

								const numChildren = object.children.length;
								for (let i = numChildren - 1; i >= 0; i--)
								{
									const child = object.children[i];
									newRoot.attach(child);
								}

								if(object.parent)
								{
									object.parent.add(newRoot);
									object.parent.remove(object);
								}

								SceneObjectUtils.disposeObject(object, false);
								return newRoot;
							}
						}

						if (gltf.scene.children.length > 1)
						{
							bounds.makeEmpty();
							bounds.expandByObject(gltf.scene);
							bounds.getSize(boundSize);
							bounds.getCenter(boundsCenter);
							const modelRootGeometry = new THREE.BoxGeometry(boundSize.x, boundSize.y, boundSize.z);
							modelRootGeometry.translate(boundsCenter.x, boundsCenter.y, boundsCenter.z);
							assetRoot = new THREE.Mesh(modelRootGeometry, CustomMaterials.getModelRootMaterial());

							const numChildren = gltf.scene.children.length;
							for (let i = numChildren - 1; i >= 0; i--)
							{
								const child = gltf.scene.children[i];
								assetRoot.attach(child);
							}
						}
						else
						{
							assetRoot = gltf.scene.children.length ? gltf.scene.children[0] : new THREE.Object3D();
						}

						const modelRoot = convertOrGenerateModelRoot(assetRoot);
						modelRoot.name = name;
						if (gltf.animations.length > 0) modelRoot.userData.animations = gltf.animations;

						const lights: THREE.Light[] = [];
						const cameras: THREE.Camera[] = [];
						const gatheredMaterials: Map<string, THREE.Mesh[]> = new Map();
						const convertToModelRoots: THREE.Object3D[] = [];
						modelRoot.traverse((child) =>
						{
							child.userData.isCachedAssetObject = true;
							child.layers.enable(RenderLayers.ObjectPreview);
							child.userData.originalUuid = child.uuid;
							child.userData.guid = getUuidv4();
							child.userData.assetId = id;
							child.userData.visibility =
						{
							overrideParent: false,
							standalone: VisibilityModes.Solid,
							device: VisibilityModes.Solid
						};

							SceneObjectUtils.initialiseObjectMultipassRenderingCallback(child);

							if(child.userData.metaData)
							{
								if(child.userData.metaData.WLC_JT_ROOT)
								{
									convertToModelRoots.push(child);
									delete child.userData.metaData.WLC_JT_ROOT;
								}
								MetadataManager.registerEmbeddedMedataKeys(child.userData.metaData);
							}

							if (child.id !== modelRoot.id)
							{
								child.userData.isSubMesh = true;
							}

							if (child instanceof THREE.Mesh && child.material && child.material.visible)
							{
								if (!gatheredMaterials.has(child.material.userData.guid))
								{
									gatheredMaterials.set(child.material.userData.guid, []);
								}
								gatheredMaterials.get(child.material.userData.guid)!.push(child);
							}

							if (child instanceof THREE.SkinnedMesh)
							{
							//injecting a guid to skeletons to allow the gltfexporter to identify identical skeletons and not export duplicate data
								(child.skeleton as any).guid = getUuidv4();
							}
							else if (child instanceof THREE.Light)
							{
								lights.push(child);
							}
							else if (child instanceof THREE.Camera)
							{
								cameras.push(child);
							}
						});

						for(const object of convertToModelRoots)
						{
							convertOrGenerateModelRoot(object);
						}

						modelRoot.layers.disable(RenderLayers.ObjectPreview);

						for (const camera of cameras)
						{
							const scopeCamera = new ScopeCamera();

							if (camera instanceof THREE.PerspectiveCamera)
							{
								scopeCamera.userData.camera.fov = camera.fov;
								scopeCamera.userData.camera.nearplane = camera.near;
								scopeCamera.userData.camera.farPlane = camera.far;
								scopeCamera.setCameraProjectionMode(CameraProjectionModes.Perspective);
							}
							else if (camera instanceof THREE.OrthographicCamera)
							{
								scopeCamera.userData.camera.orthographicSize = camera.left;
								scopeCamera.userData.camera.nearplane = camera.near;
								scopeCamera.userData.camera.farPlane = camera.far;
								scopeCamera.setCameraProjectionMode(CameraProjectionModes.Orthographic);
							}

							camera.parent!.add(scopeCamera);
							scopeCamera.position.set(camera.position.x, camera.position.y, camera.position.z);
							scopeCamera.rotation.set(camera.rotation.x, camera.rotation.y, camera.rotation.z);
							scopeCamera.scale.set(camera.scale.x, camera.scale.y, camera.scale.z);

							for (const [key, value] of Object.entries(camera.userData))
							{
								scopeCamera.userData[key] = value;
							}

							camera.parent!.remove(camera);
							for (const child of camera.children)
							{
								scopeCamera.add(child);
							}
						}

						for (const light of lights)
						{
							let scopeLight: ScopeLight | THREE.Object3D | undefined = undefined;
							switch (light.type)
							{
								case 'DirectionalLight':
									scopeLight = new ScopeLight(LightTypes.Directional);
									break;

							// case 'PointLight':
							// 	scopeLight = new ScopeLight(LightTypes.Point);
							// 	scopeLight.setLightRange(light.distance);
							// 	break;
							// case 'SpotLight':
							// 	scopeLight = new ScopeLight(LightTypes.Spot);
							// 	scopeLight.setLightRange(light.distance);
							// 	scopeLight.setLightAngle(light.angle);
							// 	break;
							// default:
							// 	console.warn("unknown type: " + light.type);
							// 	break;
							}

							if (scopeLight)
							{
								(scopeLight as ScopeLight).setLightColor(light.color.getHexString());
								(scopeLight as ScopeLight).setLightIntensity(light.intensity);
							}
							else
							{
								scopeLight = new THREE.Object3D();
							}

							light.parent!.add(scopeLight);
							scopeLight.position.set(light.position.x, light.position.y, light.position.z);
							scopeLight.rotation.set(light.rotation.x, light.rotation.y, light.rotation.z);
							scopeLight.scale.set(light.scale.x, light.scale.y, light.scale.z);
							scopeLight.name = light.name;

							for (const [key, value] of Object.entries(light.userData))
							{
								scopeLight.userData[key] = value;
							}

							light.parent!.remove(light);
							for (const child of light.children)
							{
								scopeLight.add(child);
							}
						}

						if (generateMaterialAssets)
						{
							for (const objectArray of gatheredMaterials.values())
							{
								const currentMaterial = objectArray[0].material as THREE.MeshStandardMaterial;
								const existingAsset = currentMaterial.userData.guid ?
									MaterialAsset.getMaterialAssetByGuid(currentMaterial.userData.guid) :
									MaterialAsset.defaultMaterial;

								const materialAsset = existingAsset ? existingAsset : new MaterialAsset({ type: MaterialTypes.Standard, sourceMaterial: currentMaterial });
								for (const object of objectArray)
								{
									const previousMaterial = object.material as THREE.Material;
									MaterialAsset.assignMaterialAssetToObject(materialAsset, object);
									previousMaterial.dispose();
								}
							}
						}
						else
						{
							gatheredMaterials.forEach(function (objectArray, materialGuid)
							{
								let materialAsset = MaterialAsset.getMaterialAssetByGuid(materialGuid);
								if (!materialAsset) materialAsset = MaterialAsset.defaultMaterial;

								for (const object of objectArray)
								{
									const previousMaterial = object.material as THREE.Material;
									MaterialAsset.assignMaterialAssetToObject(materialAsset, object);
									previousMaterial.dispose();
								}
							});
						}

						this.modelBank.set(id, modelRoot);

						let modelInstance: Object3D | undefined = undefined;
						if (addToScene)
						{
							modelInstance = SceneObjectUtils.cloneObject(modelRoot);
							new AddObjectAction(modelInstance!, startingPosition);
						}

						if (onComplete)
						{
							onComplete(id, url, name, modelInstance);
						}
						StandbyOverlay.stopStandbyMode();
						resolve();
					}, 0);
				},
				function (xhr)
				{
					console.log("loading model " + Math.round(xhr.loaded / xhr.total * 100.0) + "%");
					if (onDownloadProgress)
					{
						onDownloadProgress(id, url, name, xhr.loaded / xhr.total);
					}
				},
				function (error)
				{
					if (error instanceof Error && error.message.includes('abort'))
					{
						if (onComplete)
						{
							onComplete(id, url, name, undefined);
						}
						return;
					}
					if (error instanceof Error) 
					{
						console.error('Could not load model ' + id + " " + error.message);
					}
					else 
					{
						console.error('Could not load model ' + id + " " + String(error));
					}
					reject();
				}
				);
		});
	}

	setBackgroundMap(cubemap: THREE.CubeTexture)
	{
		this._backgroundMap = cubemap;
		const renderBackgroundMap = store.getState().preferencesModule.editorPreferences.renderDefaultBackground;
		if(renderBackgroundMap) this._backgroundScene.background = cubemap;
	}

	setEnvironmentMap = (cubemap: THREE.CubeTexture) =>
	{
		this.environmentMap = cubemap;
		if(this.currentScene) this.currentScene.environment = cubemap;
	}

	createModel = async (id: string, url: string, name: string,
		startingPosition? :THREE.Vector3,
		onDownloadProgress? : (id: string, url: string, name: string, progress: number) => void,
		onComplete? : (id: string, url: string, name: string, model?: Object3D) => void) =>
	{
		if (this.modelBank.has(id))
		{
			const modelInstance = SceneObjectUtils.cloneObject(this.modelBank.get(id)!);
			new AddObjectAction(modelInstance, startingPosition);

			if (onComplete)
			{
				onComplete(id, url, name, modelInstance);
			}
		}
		else
		{
			try 
			{
				await this.prepareModel(id, url, name, startingPosition, true, true, onDownloadProgress, onComplete);
			}
			catch (error)
			{
				console.error(`Could not load model ${id}`);
			}
		}
	}

	clearProjectData = () =>
	{
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onUnloadScene));
		Timeline.clearTimelinesData();

		if (this.pickingManager) this.pickingManager.clearSelection();

		this.currentScene = undefined;
		this.currentStep = undefined;
		this.pendingStepChange = undefined;
		this.previewCamera = undefined;

		for (const scene of this.sceneList)
		{
			const selectionRoot = this.getSelectionRoot(scene.userData.guid);
			SceneObjectUtils.disposeObject(selectionRoot);
		}
		this.sceneList.length = 0;

		for (const model of this.modelBank.values())
		{
			SceneObjectUtils.disposeObject(model);
		}

		this.modelBank.clear();
		this.objectMixerMap.clear();
		this.animatedObjects.clear();

		CubemapUtils.disposeCubemaps();
		const defaultCubemap = CubemapUtils.getCubemap(BuiltInCubeMaps.Default);
		if(defaultCubemap) this.setEnvironmentMap(defaultCubemap);
		DefaultSceneManager.getInstance().clearData();
		GroupsManager.clearData();
		MaterialAsset.clearData();
		AudioSource.clearData();
		ScopeCamera.clearData();
		Callout.clearData();
		ARTracker.clearData();
		Text3D.clearData();
		if(this.billboardManager) this.billboardManager.clearData();
		this.toolsManager.clearData();
		ActionManager.clearHistory();
		AssetsManager.clearUsedAssets();
		ScenarioHealthUtils.ClearReports();
		Scenario.clear();
		MetadataManager.clearData();
		this.renderer.renderLists?.dispose();
	}

	setPreviewCamera = (camera: ScopeCamera) =>
	{
		this.previewCamera = camera;
	}

	setPreviewCameraFromSelection = () =>
	{
		// there can be only one camera preview window, only if a single camera is selected. if multiple are selected, we don't show any preview windows
		const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects as THREE.Object3D[];
		const selectedCameras = selectedObjects.filter(o => o.userData.camera)
		
		if(selectedCameras.length === 1)
		{
			WorkspaceManager.openWindow(ApplicationModuleTypes.CameraPreview);
		}
	}

	preloadAssets = async (): Promise<void> =>
	{
		const assetPromises = [
			CubemapUtils.loadCubemap(BuiltInCubeMaps.Default),
			CubemapUtils.loadCubemap(BuiltInCubeMaps.Interior),
			CubemapUtils.loadCubemap(BuiltInCubeMaps.Outside),
			CubemapUtils.loadCubemap(BuiltInCubeMaps.Studio),
			Text3D.loadFont(),
			ARTracker.loadDefaultTrackerImage(),
			ScopeLight.loadSpriteMaterial('../bulb.png'),
			ScopeCamera.loadSpriteMaterial('../camera.png'),
			AudioSource.loadSpriteMaterial('../audio.png')
		]

		await Promise.all(assetPromises);
	}

	getActiveSceneViewId = () =>
	{
		return this.activeSceneViewId;
	}

	getActiveSceneView = () : ISceneViewData | undefined =>
	{
		const activeSceneViewData = WorkspaceManager.getSceneViewData(this.activeSceneViewId);
		return activeSceneViewData || this.getMainSceneView();
	}

	getMainSceneView = () : ISceneViewData | undefined =>
	{
		const mainSceneViewId = store.getState().workspaceModule.mainSceneViewId;
		const mainSceneViewData = WorkspaceManager.getSceneViewData(mainSceneViewId);
		return mainSceneViewData;
	}

	setMainSceneView = (sceneViewId: string) =>
	{
		this.setActiveSceneView(sceneViewId);
		store.dispatch(StoreActions.workspace.setMainSceneView({ id: sceneViewId }));
	}

	setActiveSceneView = (sceneViewId: string) =>
	{
		if (this.activeSceneViewId !== sceneViewId)
		{
			this.activeSceneViewId = sceneViewId;
			this.getCameraController()!.resetMouseDelta();
		}

		const activeCamera = WorkspaceManager.getSceneViewData(this.activeSceneViewId)?.activeCamera;
		if(activeCamera)
		{
			this.getGizmoManager().setCamera(activeCamera);
			this.getPickingManager().setCamera(activeCamera);
		}
	}

	setDisplayUnitScale = (unitScale: WorldUnitScales) =>
	{
		this.displayUnitScale = unitScale;
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onDisplayUnitChange));
	}

	setSpritesVisibility = (visible: boolean) =>
	{
		if (AudioSource.spriteMaterial) AudioSource.spriteMaterial.visible = visible;
		if (ScopeLight.spriteMaterial) ScopeLight.spriteMaterial.visible = visible;
		if (ScopeCamera.spriteMaterial) ScopeCamera.spriteMaterial.visible = visible;
	}

	getDisplayUnitScale = () : WorldUnitScales =>
	{
		return this.displayUnitScale;
	}

	getCamera = () : THREE.Camera | undefined =>
	{
		return this.getActiveSceneView()?.activeCamera;
	}

	getRenderCamera = () : Camera | undefined =>
	{
		return this.renderCamera;
	}

	registerObjectAnimationsForExport = (object : THREE.Object3D) =>
	{
		assert(object.userData.animations);
		if (!DefaultSceneManager.getInstance().isDefaultObject(object.userData.guid))
		{
			this.animatedObjects.add(object);
		}
	}

	deregisterObjectAnimationsForExport = (object: THREE.Object3D) =>
	{
		if(this.animatedObjects.has(object))
		{
			this.animatedObjects.delete(object);
		}
	}

	getExportAnimations = () : THREE.AnimationClip[] =>
	{
		let exportAnimations = [];
		for (const animatedObject of this.animatedObjects)
		{
			exportAnimations = exportAnimations.concat(animatedObject.userData.animations);
		}

		return exportAnimations;
	}

	createAnimationMixer = (object : THREE.Object3D) =>
	{
		assert(object.userData.animations);
		this.objectMixerMap.set(object.userData.guid, new THREE.AnimationMixer(object));
	}

	deleteAnimationMixer = (object: THREE.Object3D) =>
	{
		assert(this.objectMixerMap.has(object.userData.guid));
		this.objectMixerMap.delete(object.userData.guid);
	}

	getAnimationMixer = (objectGuid: string) : THREE.AnimationMixer | undefined =>
	{
		return this.objectMixerMap.get(objectGuid);
	}

	getScene = (sceneId?: string) : THREE.Scene | null =>
	{
		return this.sceneList.find(scene => scene.userData.guid === sceneId) || null;
	}

	getSceneList = () : THREE.Scene[] =>
	{
		return this.sceneList;
	}

	getSelectionRoot = (sceneId?: string) : THREE.Object3D =>
	{
		const targetScene = sceneId ? this.getScene(sceneId) : this.currentScene;
		if (targetScene)
		{
			for (const child of targetScene.children)
			{
				if (child.name === 'root') return child;
			}
		}

		return new THREE.Object3D();
	}

	getAudioListener = () : THREE.AudioListener =>
	{
		return this.audioListener;
	}

	getCurrentScene = () : THREE.Scene | undefined =>
	{
		return this.currentScene;
	}

	getPickingManager = () : PickingManager =>
	{
		return this.pickingManager;
	}

	getPostProcessManager = () : PostProcessManager =>
	{
		return this.postProcessManager;
	}

	getInteractiveModeManager = () : InteractiveModeManager =>
	{
		return this.interactiveModeManager;
	}

	getToolsManager = () : ToolsManager =>
	{
		return this.toolsManager;
	}

	getGizmoManager = () : GizmoManager =>
	{
		return this.gizmoManager;
	}

	getBillboardManager = () : BillboardManager =>
	{
		return this.billboardManager;
	}

	getCameraController = () : SceneCameraController | undefined =>
	{
		return this.getActiveSceneView()?.cameraController;
	}

	getRenderer = () : THREE.WebGLRenderer =>
	{
		return this.renderer;
	}

	getModelAsset = (assetId: string) : THREE.Object3D | undefined =>
	{
		return this.modelBank.get(assetId);
	}

	getGrid = () : THREE.Mesh =>
	{
		return this.grid;
	}

	getTriggerManager = () : TriggerManager =>
	{
		return this.triggerManager;
	}

	getSceneRecorder = () : SceneRecorder =>
	{
		return this.sceneRecorder;
	}

	setlegacyLightingMode = (enabled: boolean) =>
	{
		this.legacyLightingMode = enabled;
	}
}
